import { formatPaymentTerms, parsePaymentTerms, PAYMENT_TERM_PRESETS, PAYMENT_TERMS_NOT_STATED, type PaymentTerm, termIsPi } from '@crm/shared';
import { useState } from 'react';
import { Icon } from './Icon';

const blank: PaymentTerm = { name: '', percent: 0, creditDays: null, docType: null };

/**
 * The customer PO's payment terms, picked instead of typed: one click for the common terms, then a row per payment
 * (name, % of the contract, credit days, PI or tax invoice) with the running total, or "Not stated in the PO". The value
 * is the text saved on the deal (see parsePaymentTerms): the project's billing milestones, and PI or tax invoice for each.
 */
export function PaymentTermsPicker({ value, onChange, id = 'terms' }: { value: string; onChange: (text: string) => void; id?: string }) {
  const parsed = parsePaymentTerms(value);
  // Rows being edited: kept here so a half-typed row (no name yet) isn't lost on the way through the text.
  const [rows, setRows] = useState<PaymentTerm[]>(() => (parsed.terms.length ? parsed.terms : []));
  const set = (next: PaymentTerm[]) => { setRows(next); onChange(formatPaymentTerms(next.filter((r) => r.name.trim() && r.percent > 0))); };
  const edit = (i: number, patch: Partial<PaymentTerm>) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const total = Math.round(rows.reduce((a, r) => a + (r.percent || 0), 0) * 100) / 100;
  const preset = PAYMENT_TERM_PRESETS.find((p) => formatPaymentTerms(p.terms) === formatPaymentTerms(rows));
  if (parsed.notStated) {
    return (
      <div className="terms-picker">
        <label className="terms-none"><input type="checkbox" checked onChange={() => { setRows([]); onChange(''); }} /> {PAYMENT_TERMS_NOT_STATED} <span className="small muted">(the project uses its type’s default terms)</span></label>
      </div>
    );
  }
  return (
    <div className="terms-picker">
      <div className="terms-presets" role="group" aria-label="Common payment terms">
        {PAYMENT_TERM_PRESETS.map((p) => (
          <button key={p.key} type="button" className={`chip${preset?.key === p.key ? ' on' : ''}`} aria-pressed={preset?.key === p.key} onClick={() => set(p.terms.map((t) => ({ ...t })))}>{p.label}</button>
        ))}
        <button type="button" className={`chip${rows.length && !preset ? ' on' : ''}`} onClick={() => set(rows.length ? rows : [{ ...blank }])}>Custom</button>
      </div>
      {rows.length > 0 && (
        <table className="terms-rows">
          <thead><tr><th>Payment</th><th className="num">%</th><th className="num">Credit days</th><th>Billed with</th><th /></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i}>
              <td><input id={i === 0 ? id : undefined} value={r.name} maxLength={120} placeholder="e.g. Material delivery" aria-label={`Payment ${i + 1} name`} onChange={(e) => edit(i, { name: e.target.value })} /></td>
              <td className="num"><input type="number" min={0} max={100} step={0.5} value={r.percent || ''} aria-label={`Payment ${i + 1} percent`} onChange={(e) => edit(i, { percent: Number(e.target.value) })} /></td>
              <td className="num"><input type="number" min={0} max={365} value={r.creditDays ?? ''} placeholder="—" aria-label={`Payment ${i + 1} credit days`}
                onChange={(e) => edit(i, { creditDays: e.target.value === '' ? null : Number(e.target.value) })} /></td>
              <td><select value={termIsPi(r) ? 'pi' : 'tax'} aria-label={`Payment ${i + 1} billed with`} onChange={(e) => edit(i, { docType: e.target.value as 'pi' | 'tax' })}>
                <option value="pi">PI first</option><option value="tax">Tax invoice</option></select></td>
              <td><button type="button" className="icon-btn" aria-label={`Remove payment ${i + 1}`} onClick={() => set(rows.filter((_, j) => j !== i))}><Icon name="close" /></button></td>
            </tr>
          ))}</tbody>
          <tfoot><tr>
            <td><button type="button" className="link-btn small" onClick={() => set([...rows, { ...blank }])}>+ Add a payment</button></td>
            <td className={`num terms-total ${Math.abs(total - 100) < 0.01 ? 'ok' : 'bad'}`}>{total}%{Math.abs(total - 100) < 0.01 ? ' ✓' : ''}</td>
            <td colSpan={3} className="small muted">{Math.abs(total - 100) < 0.01 ? 'Adds up to 100%: these become the project’s billing milestones' : 'The payments should add up to 100%'}</td>
          </tr></tfoot>
        </table>
      )}
      <label className="terms-none"><input type="checkbox" checked={false} onChange={() => { setRows([]); onChange(PAYMENT_TERMS_NOT_STATED); }} /> {PAYMENT_TERMS_NOT_STATED}</label>
    </div>
  );
}

/** "Advance 10% PI · Material delivery 70% (30 d) · Handover 20% (30 d)": the terms in one line. */
export const termsSummary = (text: string | null | undefined) => {
  const p = parsePaymentTerms(text);
  if (p.notStated) return PAYMENT_TERMS_NOT_STATED;
  return p.terms.map((t) => `${t.name} ${t.percent}%${t.creditDays != null ? ` (${t.creditDays} d)` : ''}${termIsPi(t) ? ' · PI' : ''}`).join(' · ');
};
